import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService } from '@angular-workspace/core';
import { FILE_BASE_URL } from '../controlplane/file-api.service';

/**
 * Injection token for Resource Server API base URL
 */
export const RS_BASE_URL = new InjectionToken<string>('RS_BASE_URL');

/**
 * Resource Server API Service for handling NGSI-LD entity-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class RsApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(RS_BASE_URL) private baseRsPathUrl: string,
    @Inject(FILE_BASE_URL) private fileRsServer: string,
  ) {}

  /**
   * Get NGSI-LD entity by dataset ID
   * @param datasetId The ID of the dataset
   * @param page Page number (default: 1)
   * @returns Promise with entity data
   */
  async entitiesNgsiLg(datasetId: string, page: number = 1): Promise<any> {
    try {
      // Construct the full URL: baseRsPathUrl/ngsi-ld/v2/entities/<id>?page=1
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/entities/${datasetId}?page=${page}`;
      
      // Make GET request using NetworkService
      const response = await this.networkService.get_api(url);
      return response;
    } catch (error) {
      console.error('Error fetching NGSI-LD entity:', error);
      throw error;
    }
  }

  /**
   * Download CSV file for a dataset
   * @param datasetId The ID of the dataset
   * @returns Promise with blob data for CSV download
   */
  async downloadCSV(datasetId: string): Promise<Blob> {
    try {
      // Construct the full URL: baseRsPathUrl/ngsi-ld/v2/<datasetId>/download
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/${datasetId}/download`;
      
      // Make GET request with blob response type for file download
      const headers = { responseType: 'blob' };
      const response = await this.networkService.get_api(url, headers);
      return response;
    } catch (error) {
      console.error('Error downloading CSV:', error);
      throw error;
    }
  }

  /**
   * Download CSV file for a dataset using NGSI-LD endpoint from file server
   * @param datasetId The ID of the dataset
   * @returns Promise with blob data for CSV download
   */
  async downloadCSVNgslid(datasetId: string): Promise<Blob> {
    try {
      // Construct the full URL: fileRsServer/ngsi-ld/v2/<datasetId>/download
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/${datasetId}/download`;
      
      // Make GET request with blob response type for file download
      const headers = { responseType: 'blob' };
      const response = await this.networkService.get_api(url, headers);
      return response;
    } catch (error) {
      console.error('Error downloading CSV via NGSI-LD:', error);
      throw error;
    }
  }
}
